"use client";

import Image from "next/image";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, useMotionValue, useAnimationFrame } from "framer-motion";
import AvatarArray from "@/assets/images/testimonials/avatars/avatar.webp";
import { data } from "@/data";
import { Smoothup } from "@/components/common/animations";
import Testimony from "@/components/Testimony";

function Carousel({ items, reverse = false }: { items: typeof data.testimonials.data; reverse?: boolean }) {
  const [isPaused, setIsPaused] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const x = useMotionValue(0);

  // Measure content width on mount and resize
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const firstHalf = containerRef.current.scrollWidth / 2;
        setContentWidth(firstHalf);
        if (reverse) {
          x.set(-firstHalf);
        }
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [items, reverse, x]);

  // Animation frame loop
  useAnimationFrame((_, delta) => {
    if (isPaused || contentWidth === 0) return;

    const speed = contentWidth / 60000; // 60s for full cycle
    const movement = speed * delta;

    let newX = x.get() + (reverse ? movement : -movement);

    if (!reverse && newX <= -contentWidth) {
      newX = 0;
    } else if (reverse && newX >= 0) {
      newX = -contentWidth;
    }

    x.set(newX);
  });

  // Desktop: hover handlers
  const handleMouseEnter = useCallback((index: number) => {
    setIsPaused(true);
    setActiveCard(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
    setActiveCard(null);
  }, []);

  // Mobile: touch handlers (tap and hold)
  const handleTouchStart = useCallback((index: number) => {
    setIsPaused(true);
    setActiveCard(index);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsPaused(false);
    setActiveCard(null);
  }, []);

  return (
    <div className="carousel-container carousel-fade">
      <motion.div
        ref={containerRef}
        style={{ x }}
        className="flex gap-3 md:gap-5 items-center w-max"
      >
        {Array(2)
          .fill(items)
          .flat()
          .map((testimonial, index) => (
            <motion.div
              key={index}
              animate={{
                scale: activeCard === index ? 1.02 : 1,
              }}
              transition={{ duration: 0.2 }}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onTouchStart={() => handleTouchStart(index)}
              onTouchEnd={handleTouchEnd}
              className="touch-none"
            >
              <Testimony
                index={index}
                desc={testimonial.desc}
                name={testimonial.name}
                imgSrc={testimonial.imgSrc}
                age={testimonial.age}
              />
            </motion.div>
          ))}
      </motion.div>
    </div>
  );
}

export default function TestimonialsSection() {
  return (
    <section
      id="testimonials"
      className="max-w-7xl xl:mx-auto px-4 xl:px-0 pb-16"
    >
      <Smoothup className="flex flex-col items-center gap-4 text-[#171717]">
        <Image
          src={AvatarArray}
          alt="Array of avatars"
          className="w-[200px] h-[45px]"
        />
        <h1 className="md:text-[46px] md:font-bold md:leading-[55.2px] tracking-[-0.02em] text-center text-[#171717] text-[2.5rem] font-bold leading-[2.75rem]">
          {data.testimonials.heading}
        </h1>
        <p className="text-lg font-medium tracking-[-0.02em] text-center text-black/60 max-w-md">
          {data.testimonials.subheading}
        </p>
      </Smoothup>
      <Smoothup className="flex flex-col items-center gap-3 text-[#171717]">
        <div className="w-full pt-8 md:pt-10 lg:pt-20 space-y-3 md:space-y-5">
          <Carousel items={data.testimonials.data2} />
          <Carousel items={data.testimonials.data} reverse />
        </div>

        <p className="text-center text-xs text-black/40 mt-12 max-w-3xl leading-relaxed">
          Names have been changed to protect client privacy. Testimonials are
          based on unique experiences from current clients and are not
          representative of all client experiences. Testimonials are
          unsolicited, and no cash or non-cash compensation is provided.
          Investing involves risk, including the possible loss of principal and
          fluctuation of value. Past performance is no guarantee of future
          results. Additional information about Eden Financial Technologies Inc.
          DBA Cove is available in its current disclosure documents, Form ADV,
          Form ADV Part 2A Brochure, and Client Relationship Summary report
          which are accessible online via the SEC&apos;s Investment Adviser Public
          Disclosure (IAPD) database at{" "}
          <a
            href="https://www.adviserinfo.sec.gov"
            target="_blank"
            className="text-black/40 underline hover:text-black/60 transition-colors"
          >
            IAPD
          </a>
          , using SEC # 801-130646.
        </p>
      </Smoothup>
    </section>
  );
}